import * as mutation from "./mutation-types";
import * as action from "./action-types";
import { tableHelper } from "@/helpers/table.helper";
import { deepCopyAxiosException } from "@/store/helpers";
import { SHOW_NOTIFICATION } from "@/store/notifications/mutation-types";
import { GLOBAL_ERROR } from "@/store/system/mutation-types";
import axios from "axios";

export const actions = {
  async [action.LIST]({ commit, state }) {
    commit(mutation.LIST_STARTED);
    try {
      const url = tableHelper.constructUrl("/api/prisms-users", state.listing.paging, state.listing.criteria);
      const { headers, data } = await axios.get(url);
      commit(mutation.LIST_SET_CRITERIA_DESCRIPTION, tableHelper.getCriteriaInfo(headers));
      commit(mutation.LIST_SET_PAGING, tableHelper.getPagingFromHeaders(headers));
      commit(mutation.LIST_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.LIST_FAILED, deepCopyAxiosException(e));
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.LIST_NEXT_PAGE]({ state, commit, dispatch }) {
    commit(mutation.LIST_SET_PAGING, { page: state.listing.paging.page + 1 });
    await dispatch(action.LIST);
  },
  async [action.LIST_PREVIOUS_PAGE]({ state, commit, dispatch }) {
    commit(mutation.LIST_SET_PAGING, { page: state.listing.paging.page - 1 });
    await dispatch(action.LIST);
  },

  async [action.VIEW]({ commit, state }, id) {
    commit(mutation.VIEW_STARTED, id);
    try {
      const url = `/api/prisms-users/${id}`;
      const { data } = await axios.get(url);
      commit(mutation.VIEW_SUCCEEDED, data);
      const index = tableHelper.getIndexOfId(state.listing, state.view.data.id);
      commit(mutation.VIEW_NEXT, tableHelper.getNextRecordInfo(index, state.listing));
      commit(mutation.VIEW_PREVIOUS, tableHelper.getPreviousRecordInfo(index, state.listing));
      return true;
    } catch (e) {
      commit(mutation.VIEW_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.VIEW_REFRESH]({ state, dispatch }, userId) {
    const needsRefreshed = (!state.view.loaded && !state.view.loading) || state.view.userId != userId;
    if (needsRefreshed) {
      await dispatch(action.VIEW, userId);
    }
  },

  async [action.EXPORT]({ commit, state }) {
    commit(mutation.EXPORT_STARTED);
    try {
      await axios.get(tableHelper.constructUrl("/api/exports/validate/prisms-users", state.listing.criteria));
      commit(SHOW_NOTIFICATION, "Your export will commence shortly.");
      const url = tableHelper.constructUrl(`/api/exports/prisms-users`, state.listing.criteria);
      tableHelper.downloadExportedSpreadsheet(url);
      commit(mutation.EXPORT_SUCCEEDED);
      return true;
    } catch (e) {
      commit(mutation.EXPORT_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.EDIT_GET]({ commit }, id) {
    commit(mutation.EDIT_GET_STARTED);
    try {
      const url = `/api/prisms-users/${id}`;
      const { data } = await axios.get(url);
      commit(mutation.EDIT_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.EDIT_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_SAVE]({ commit, state }) {
    commit(mutation.EDIT_SAVE_STARTED);
    try {
      const data = state.edit.data;
      const result = await axios.patch(`/api/prisms-users/${data.id}`, data);
      commit(mutation.EDIT_SAVE_SUCCEEDED, result.data);
      commit(mutation.STATE_UNLOAD);
      commit(SHOW_NOTIFICATION, "User updated");
      return true;
    } catch (e) {
      commit(mutation.EDIT_SAVE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.ENABLE_MFA]({ commit }, userId) {
    commit(mutation.ENABLE_MFA_STARTED);
    try {
      const endpointUrl = `/api/users/${userId}/enable-mfa`;
      await axios.post(endpointUrl);
      commit(mutation.ENABLE_MFA_SUCCEEDED);
      commit(SHOW_NOTIFICATION, "Enabled user's multi-factor authentication.");
      return true;
    } catch (e) {
      commit(mutation.ENABLE_MFA_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.DISABLE_MFA]({ commit }, message) {
    commit(mutation.DISABLE_MFA_STARTED);
    try {
      const endpointUrl = `/api/users/${message.userId}/disable-mfa`;
      const result = await axios.post(endpointUrl, message);
      commit(mutation.DISABLE_MFA_SUCCEEDED);
      commit(SHOW_NOTIFICATION, "Disabled user's multi-factor authentication.");
      return true;
    } catch (e) {
      commit(mutation.DISABLE_MFA_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.SWITCH_TO_EMAIL_MFA]({ commit }, message) {
    commit(mutation.SWITCH_TO_EMAIL_MFA_STARTED);
    try {
      const endpointUrl = `/api/users/${message.userId}/switch-to-email-mfa`;
      const result = await axios.post(endpointUrl, message);
      commit(mutation.SWITCH_TO_EMAIL_MFA_SUCCEEDED);
      commit(SHOW_NOTIFICATION, "Switched user's multi-factor authentication method to email.");
      return true;
    } catch (e) {
      commit(mutation.SWITCH_TO_EMAIL_MFA_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.VIEW_MFA_PROFILE]({ commit }) {
    commit(mutation.VIEW_MFA_PROFILE_STARTED);
    try {
      const url = `/api/profile/mfa`;
      const { data } = await axios.get(url);
      commit(mutation.VIEW_MFA_PROFILE_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.VIEW_MFA_PROFILE_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.SETUP_AUTHENTICATOR_APP_MFA_GET]({ commit } ) {
    commit(mutation.SETUP_AUTHENTICATOR_APP_MFA_GET_STARTED);
    try {
      const url = `/api/profile/setup-authenticator-app`;
      const { data } = await axios.get(url);
      commit(mutation.SETUP_AUTHENTICATOR_APP_MFA_GET_SUCCEEDED, data);
      return true;
    } catch(e) {
      commit(mutation.SETUP_AUTHENTICATOR_APP_MFA_GET_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.SETUP_AUTHENTICATOR_APP_MFA]({ commit }, data) {
    commit(mutation.SETUP_AUTHENTICATOR_APP_MFA_STARTED);
    try {
      const url = `/api/profile/setup-authenticator-app`;
      await axios.post(url, data);
      commit(mutation.SETUP_AUTHENTICATOR_APP_MFA_SUCCEEDED);
      commit(SHOW_NOTIFICATION, "Authenticator app setup as multi-factor authentication method.");
      commit(mutation.STATE_UNLOAD);
      return true;
    } catch (e) {
      commit(mutation.SETUP_AUTHENTICATOR_APP_MFA_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.SETUP_EMAIL_MFA]({ commit }) {
    try {
      const url = `/api/profile/switch-to-email-mfa`;
      await axios.post(url);
      commit(SHOW_NOTIFICATION, "Email setup as multi-factor authentication method.");
      commit(mutation.STATE_UNLOAD);
      return true;
    } catch(e) {
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  }
};
